import { useCallback, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarRange, Check, ChevronDown } from 'lucide-react';
import { Abas, PainelAba } from '../abas';
import { Botao } from '../base';
import { CampoData } from '../formulario';
import { useCamadaFlutuante } from '../menu';
import { useValidacaoLocal } from '../validacao-local';
import { data, nomeDoMes } from '../../nucleo/formato';
import { ABAS_PERIODO, ATALHOS_PERIODO, abaDoPeriodo, aplicarIntervalo, aplicarMes, mesDoPeriodo, type AbaPeriodo, type EscolhaPeriodo, type Periodo, type PeriodoGuardado } from './periodo';

/**
 * Rótulo do período. Mês inteiro do calendário (o mês atual, o passado ou um mês escolhido) sai como no antigo, "Outubro / 2026";
 * os outros atalhos saem pelo nome ("Últimos 30 dias") e o intervalo livre pelas datas no idioma do app.
 */
export function useRotuloPeriodo() {
  const { t } = useTranslation();
  return useCallback(
    (escolha: EscolhaPeriodo, p: Periodo) => {
      const mes = escolha === 'mes' || escolha === 'mes_passado' || escolha === 'personalizado' ? mesDoPeriodo(p) : null;
      if (mes) return t('periodo.mesAno', { mes: nomeDoMes(Number(mes.slice(5, 7))), ano: mes.slice(0, 4) });
      return escolha === 'personalizado' ? t('comum.intervalo', { de: data(p.de), ate: data(p.ate) }) : t(`periodo.atalhos.${escolha}`);
    },
    [t],
  );
}

type AoEscolher = (e: PeriodoGuardado) => void;

/**
 * Fase 2 (W33, item 1): conteúdo do painel, na estrutura do antigo, com as Abas do núcleo (segmentado; setas, Home e End,
 * `aria-selected` e painel ligado por `aria-controls`):
 *  - Atalhos: os sete atalhos (o antigo tinha cinco; Hoje e Esta semana ficam), com `aria-pressed` e indicador de 2px.
 *  - Mês específico: mês e ano, com "Aplicar".
 *  - Período: De e Até, com "Aplicar período".
 * Data inválida barra o Aplicar (guarda do núcleo) e o erro de mês ou de ordem e limite leva o foco à mensagem.
 * Review r25 visual (B2): "Aplicar" e "Aplicar período" são secundários; o painel não é modal e o primário da tela segue à
 * vista (Enter no campo também aplica).
 */
/** C1: opção "Todo o período" (sem datas) no topo dos atalhos, para as listas cujo padrão é sem período (A receber e A pagar). */
export interface TodoPeriodo {
  ativo: boolean;
  aoEscolher: () => void;
}

export function PainelPeriodo({ periodo, escolha, aoEscolher, abaInicial, todo }: { periodo: Periodo; escolha: EscolhaPeriodo; aoEscolher: AoEscolher; abaInicial?: AbaPeriodo; todo?: TodoPeriodo }) {
  const { t } = useTranslation();
  const [aba, setAba] = useState<AbaPeriodo>(() => abaInicial ?? abaDoPeriodo(escolha, periodo));
  const [mes, setMes] = useState(periodo.de.slice(0, 7));
  const [de, setDe] = useState(periodo.de);
  const [ate, setAte] = useState(periodo.ate);
  const [erro, setErro] = useState<'mes' | 'periodo' | null>(null);
  const idBase = useId();
  const mensagem = erro === 'mes' ? t('periodo.mesInvalido') : erro === 'periodo' ? t('periodo.invalido') : null;
  const { ref: refForm } = useValidacaoLocal(null, mensagem);

  const alerta = mensagem && (
    <p role="alert" className="text-xs text-perigo">
      {mensagem}
    </p>
  );

  return (
    <div className="flex flex-col gap-2">
      <Abas<AbaPeriodo>
        idBase={idBase}
        grupo="periodo"
        rotulo={t('periodo.rotuloAbas')}
        ativa={aba}
        fundo="bg-sup-2"
        aoMudar={(k) => {
          setAba(k);
          setErro(null);
        }}
        itens={ABAS_PERIODO.map((a) => ({ chave: a, rotulo: t(`periodo.abas.${a}`) }))}
      />
      <PainelAba idBase={idBase} chave={aba} className="flex flex-col gap-0.5">
        {aba === 'atalhos' && todo && (
          <button
            type="button"
            aria-pressed={todo.ativo}
            data-atalho="todo"
            onClick={todo.aoEscolher}
            className={`flex min-h-10 items-center gap-2 rounded border-l-2 px-2 text-left text-sm hover:bg-sup-3 focus:bg-sup-3 ${todo.ativo ? 'border-acento-forte font-semibold text-acento-forte' : 'border-transparent text-tinta-1'}`}
          >
            <span className="flex-1">{t('filtrosLista.todoPeriodo')}</span>
            {todo.ativo && <Check className="size-4" aria-hidden />}
          </button>
        )}
        {aba === 'atalhos' &&
          ATALHOS_PERIODO.map((a) => {
            const ativo = !todo?.ativo && escolha === a;
            return (
              <button
                key={a}
                type="button"
                aria-pressed={ativo}
                data-atalho={a}
                onClick={() => aoEscolher({ atalho: a })}
                className={`flex min-h-10 items-center gap-2 rounded border-l-2 px-2 text-left text-sm hover:bg-sup-3 focus:bg-sup-3 ${ativo ? 'border-acento-forte font-semibold text-acento-forte' : 'border-transparent text-tinta-1'}`}
              >
                <span className="flex-1">{t(`periodo.atalhos.${a}`)}</span>
                {ativo && <Check className="size-4" aria-hidden />}
              </button>
            );
          })}
        {aba === 'mes' && (
          <form
            ref={refForm}
            data-periodo-mes=""
            className="flex flex-col gap-2 px-1 pt-1 pb-1"
            onSubmit={(e) => {
              e.preventDefault();
              const escolhido = aplicarMes(mes);
              if (!escolhido) return setErro('mes');
              aoEscolher(escolhido);
            }}
          >
            <CampoData type="month" rotulo={t('periodo.mes')} value={mes} onChange={(e) => setMes(e.target.value)} />
            {alerta}
            <Botao type="submit" variante="secundario" tamanho="sm">
              {t('periodo.aplicar')}
            </Botao>
          </form>
        )}
        {aba === 'periodo' && (
          <form
            ref={refForm}
            data-periodo-intervalo=""
            className="flex flex-col gap-2 px-1 pt-1 pb-1"
            onSubmit={(e) => {
              e.preventDefault();
              const escolhido = aplicarIntervalo(de, ate);
              if (!escolhido) return setErro('periodo');
              aoEscolher(escolhido);
            }}
          >
            <div className="grid grid-cols-2 gap-2">
              <CampoData type="date" rotulo={t('periodo.de')} value={de} onChange={(e) => setDe(e.target.value)} />
              <CampoData type="date" rotulo={t('periodo.ate')} value={ate} onChange={(e) => setAte(e.target.value)} />
            </div>
            {alerta}
            <Botao type="submit" variante="secundario" tamanho="sm">
              {t('periodo.aplicarPeriodo')}
            </Botao>
          </form>
        )}
      </PainelAba>
    </div>
  );
}

/**
 * Fase 2 (W1; W33, item 1): seletor de período do núcleo. Botão com o calendário e o rótulo do período ("Outubro / 2026" ou o
 * atalho) e painel flutuante não modal (o mesmo mecanismo do Popover: Esc fecha e devolve o foco, clique fora e Tab para fora
 * fecham, nunca sai da janela) com as abas Atalhos, Mês específico e Período. A URL (`?de=&ate=`), o aparelho, o teto de
 * 3 anos e a validação seguem com quem usa o seletor (`periodo.ts`).
 */
export function SeletorPeriodo({ periodo, escolha, aoEscolher, todo, alvoTour }: { periodo: Periodo; escolha: EscolhaPeriodo; aoEscolher: AoEscolher; todo?: TodoPeriodo; /** U15 F2: `data-tour` do seletor, alvo da dica "aqui você filtra" (o Início). */ alvoTour?: string }) {
  const { t } = useTranslation();
  const rotular = useRotuloPeriodo();
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const id = useId();
  const fechar = useCallback(() => setAberto(false), []);
  const gatilho = useCallback(() => raiz.current?.querySelector<HTMLElement>(':scope > button'), []);
  const { aoTeclar } = useCamadaFlutuante({ aberto, fechar, raiz, painel, gatilho });
  const rotulo = todo?.ativo ? t('filtrosLista.todoPeriodo') : rotular(escolha, periodo);
  const intervalo = todo?.ativo ? rotulo : t('comum.intervalo', { de: data(periodo.de), ate: data(periodo.ate) });

  const escolher = (e: PeriodoGuardado) => {
    aoEscolher(e);
    setAberto(false);
    gatilho()?.focus();
  };

  return (
    <div ref={raiz} className="relative" data-tour={alvoTour} onKeyDown={aoTeclar}>
      <Botao
        tamanho="sm"
        icone={<CalendarRange className="size-4" aria-hidden />}
        aria-expanded={aberto}
        aria-controls={aberto ? id : undefined}
        aria-label={t('periodo.atual', { rotulo })}
        title={rotulo === intervalo ? rotulo : `${rotulo}: ${intervalo}`}
        onClick={() => setAberto((x) => !x)}
        data-seletor-periodo=""
      >
        <span className="max-w-48 truncate" title={rotulo}>
          {rotulo}
        </span>
        <ChevronDown className="size-4 text-tinta-3" aria-hidden />
      </Botao>
      {aberto && (
        <div ref={painel} id={id} role="group" aria-label={t('periodo.escolher')} data-painel-periodo="" className="absolute right-0 z-30 mt-1 w-80 rounded-lg border border-borda bg-sup-2 p-1.5 shadow-xl">
          {/* Cada abertura parte do período em vigor (os campos não guardam o que ficou pela metade). */}
          <PainelPeriodo
            periodo={periodo}
            escolha={escolha}
            aoEscolher={escolher}
            todo={
              todo && {
                ativo: todo.ativo,
                aoEscolher: () => {
                  todo.aoEscolher();
                  setAberto(false);
                  gatilho()?.focus();
                },
              }
            }
          />
        </div>
      )}
    </div>
  );
}
